// Accessibility utility functions and hooks
import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook for managing focus trap within modal/dialog components
 * @param {boolean} isActive - Whether the focus trap should be active
 * @returns {object} - Ref to attach to container and functions to manage focus
 */
export function useFocusTrap(isActive = false) {
  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    
    const focusableSelectors = [
      'button',
      '[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
      'audio[controls]',
      'video[controls]'
    ].join(',');
    
    return Array.from(containerRef.current.querySelectorAll(focusableSelectors))
      .filter(element => !element.disabled && element.tabIndex !== -1);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!isActive || event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }, [isActive, getFocusableElements]);

  useEffect(() => {
    if (isActive) {
      previousActiveElement.current = document.activeElement;
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
      document.addEventListener('keydown', handleKeyDown);
    } else {
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, handleKeyDown, getFocusableElements]);

  return { containerRef };
}

/**
 * Hook for managing skip links navigation
 * @returns {object} - Functions to handle skip link navigation
 */
export function useSkipLinks() {
  const skipToContent = useCallback(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const skipToNavigation = useCallback(() => {
    const navigation = document.getElementById('main-navigation');
    if (navigation) {
      navigation.focus();
      navigation.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const skipToFooter = useCallback(() => {
    const footer = document.getElementById('main-footer');
    if (footer) {
      footer.focus();
      footer.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return { skipToContent, skipToNavigation, skipToFooter };
}

/**
 * Hook for managing keyboard navigation
 * @param {string} direction - Navigation direction (horizontal/vertical)
 * @returns {object} - Functions to handle keyboard navigation
 */
export function useKeyboardNavigation(direction = 'horizontal') {
  const handleKeyNavigation = useCallback((event, items, currentIndex, onNavigate) => {
    const isHorizontal = direction === 'horizontal';
    const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';
    const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';
    const homeKey = 'Home';
    const endKey = 'End';

    switch (event.key) {
      case nextKey:
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % items.length;
        onNavigate(nextIndex);
        break;
      case prevKey:
        event.preventDefault();
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        onNavigate(prevIndex);
        break;
      case homeKey:
        event.preventDefault();
        onNavigate(0);
        break;
      case endKey:
        event.preventDefault();
        onNavigate(items.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (items[currentIndex] && typeof items[currentIndex].onClick === 'function') {
          items[currentIndex].onClick();
        }
        break;
      default:
        break;
    }
  }, [direction]);

  return { handleKeyNavigation };
}

/**
 * Hook for managing announcement to screen readers
 * @returns {object} - Function to announce messages and ref for live region
 */
export function useScreenReaderAnnouncements() {
  const [announcement, setAnnouncement] = useState('');
  const liveRegionRef = useRef(null);

  const announce = useCallback((message, priority = 'polite') => {
    setAnnouncement(''); // Clear first to ensure re-announcement
    setTimeout(() => {
      setAnnouncement(message);
      if (liveRegionRef.current) {
        liveRegionRef.current.setAttribute('aria-live', priority);
      }
    }, 100);
  }, []);

  return { announce, announcement, liveRegionRef };
}

/**
 * Hook for managing color contrast and theme accessibility
 * @returns {object} - Theme state and functions
 */
export function useAccessibleTheme() {
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    // Check for user's motion preferences
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(motionQuery.matches);

    const handleMotionChange = (e) => setReducedMotion(e.matches);
    motionQuery.addEventListener('change', handleMotionChange);

    // Check for high contrast preference
    const contrastQuery = window.matchMedia('(prefers-contrast: high)');
    setHighContrast(contrastQuery.matches);

    const handleContrastChange = (e) => setHighContrast(e.matches);
    contrastQuery.addEventListener('change', handleContrastChange);

    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
    };
  }, []);

  const toggleHighContrast = useCallback(() => {
    setHighContrast(prev => !prev);
  }, []);

  return { 
    highContrast, 
    reducedMotion, 
    toggleHighContrast,
    themeClasses: {
      highContrast: highContrast ? 'high-contrast' : '',
      reducedMotion: reducedMotion ? 'reduce-motion' : ''
    }
  };
}

/**
 * Utility function to generate unique IDs for ARIA labelling
 * @param {string} prefix - Prefix for the ID
 * @returns {string} - Unique ID
 */
export function generateAriaId(prefix = 'aria') {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Utility function to check if an element is visible to screen readers
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} - Whether element is accessible
 */
export function isElementAccessible(element) {
  if (!element) return false;
  
  const style = window.getComputedStyle(element);
  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0' ||
    element.hidden ||
    element.getAttribute('aria-hidden') === 'true'
  );
}

/**
 * Utility function to get accessible name for an element
 * @param {HTMLElement} element - Element to get name for
 * @returns {string} - Accessible name
 */
export function getAccessibleName(element) {
  if (!element) return '';
  
  // Check aria-label first
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel.trim();
  
  // Check aria-labelledby
  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy) {
    const labelElement = document.getElementById(labelledBy);
    if (labelElement) return labelElement.textContent.trim();
  }
  
  // Check associated label
  if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
    const label = document.querySelector(`label[for="${element.id}"]`);
    if (label) return label.textContent.trim();
  }
  
  // Return text content as fallback
  return element.textContent.trim();
}